import { useParams } from "react-router";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ChecklistQueryKeys } from "@/components/Procedures/API/ChecklistQueries";
import {
	AddLiveTagToStepRequest,
	CreateChecklistStepRequest,
	DeleteChecklistStepRequest,
	RemoveLiveTagFromStepRequest,
	RemoveManualMeasurementPointFromStepRequest,
	UpdateChecklistStepRequest,
} from "@/components/Procedures/Types/ManualChecklistTypes";

import { SATOSPortalNew } from "@/DataAdapter/Portal";

import { ProcedureQueryKeys } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

export const useCreateChecklistStep = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { mutateAsync } = useMutation({
		mutationFn: (request: CreateChecklistStepRequest) =>
			SATOSPortalNew.CreateChecklistStep(request),
		onMutate: (request) => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, checklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
		onSuccess: (request, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
	});

	return mutateAsync;
};

export const useUpdateChecklistStep = () => {
	const queryClient = useQueryClient();
	const { schemeId, id } = useParams<RouteParams>();
	const procedureId = +id!;
	const checklistId = +schemeId!;

	const { mutateAsync, isPending } = useMutation({
		mutationFn: (request: UpdateChecklistStepRequest) =>
			SATOSPortalNew.UpdateChecklistStep(request),
		onMutate: (request) => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, +variables.ChecklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables.ChecklistId],
			});
		},
		onSuccess: (request, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables.ChecklistId],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, procedureId],
			});
		},
	});

	return { mutateAsync, isPending };
};

export const useDeleteChecklistStep = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { mutateAsync } = useMutation({
		mutationFn: (request: DeleteChecklistStepRequest) =>
			SATOSPortalNew.DeleteChecklistStep(request),

		onMutate: (stepId) => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, stepId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				stepId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, variables],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, variables],
			});
		},
		onSuccess: (stepId) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, stepId],
			});
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
	});

	return mutateAsync;
};

export const useAddLiveTagToStep = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { mutateAsync } = useMutation({
		mutationFn: (request: AddLiveTagToStepRequest) =>
			SATOSPortalNew.AddLiveTagToStep(request),
		onMutate: (request) => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, checklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
		onSuccess: (request, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
	});
	return mutateAsync;
};

export const useRemoveLiveTagFromStep = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { mutateAsync } = useMutation({
		mutationFn: (request: RemoveLiveTagFromStepRequest) =>
			SATOSPortalNew.RemoveLiveTagFromStep(request),
		onMutate: (request) => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, checklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
		onSuccess: (request, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
	});
	return mutateAsync;
};

export const useRemoveManualMeasurementPointFromStep = () => {
	const queryClient = useQueryClient();

	const { mutateAsync } = useMutation({
		mutationFn: (request: RemoveManualMeasurementPointFromStepRequest) =>
			SATOSPortalNew.RemoveManualMeasurementPointFromStep(request),
		onSuccess: (_, request) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, request.ChecklistId],
			});
		},
	});

	return mutateAsync;
};
